import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { useNavigate } from 'react-router-dom';
import { ContactTable } from '../components/ContactTable';
import {
  // getEntities,
  // languages as getLanguages,
  search,
  listCampaignsApi,
  listTemplates,
  getAgents,
  getAssignedTo,
  getJobs
} from '../api/api';
import { Select } from '../components/Select';
import { MultiSelect } from '../components/MultiSelect';

// Quick search templates
const QUICK_SEARCHES = [
  {
    name: "Failed Compliance Today",
    icon: "⚠️",
    query: {
      timestampFrom: new Date().setHours(0, 0, 0, 0).toString(),
      compliance: "false"
    }
  },
  {
    name: "High Scores (90+)",
    icon: "⭐",
    query: {
      qaScoreMin: "90",
      qaScoreMax: "100"
    }
  },
  {
    name: "Assessed Calls",
    icon: "✅",
    query: {
      assessed: "true"
    }
  },
  {
    name: "Unassigned Calls",
    icon: "🔍",
    query: {
      assignedTo: "admin"
    }
  },
  {
    name: "Long Calls (>10 min)",
    icon: "⏱️",
    query: {
      durationMin: "600"
    }
  },
  {
    name: "Processing Calls",
    icon: "🔄",
    query: {
      status: "InProgress"
    }
  },
  {
    name: "Uploaded This Week",
    icon: "📅",
    query: {
      timestampFrom: getStartOfWeek().toString()
    }
  }
];

const Search = ({ setAlert }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('quick'); // quick, basic, advanced
  const [query, setQuery] = useState({});
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Filter options state
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [agents, setAgents] = useState([]);
  const [assignedToOptions, setAssignedToOptions] = useState([]);
  const [jobs, setJobs] = useState([]);

  // Existing SWR hooks
  // const { data: entities, error: entitiesError } = useSWR('/entities', getEntities);
  // const { data: languageCodes, error: languagesError } = useSWR('/languages', getLanguages);

  // Load filter options
  useEffect(() => {
    const loadOptions = async () => {
      try {
        // Use existing APIs
        const campaignsRes = await listCampaignsApi();
        setCampaigns(campaignsRes.campaigns || []);

        const templatesRes = await listTemplates();
        const templateNames = (templatesRes.templates || []).map(t => t.templateName);
        setTemplates(templateNames);
        setFilteredTemplates(templateNames);

        // Get agents using dedicated API
        const agentsRes = await getAgents();
        setAgents(Array.isArray(agentsRes) ? agentsRes : []);

        // Get assignedTo using dedicated API
        const assignedToRes = await getAssignedTo();
        setAssignedToOptions(Array.isArray(assignedToRes) ? assignedToRes : []);

        // Get jobs using dedicated API
        const jobsRes = await getJobs();
        setJobs(Array.isArray(jobsRes) ? jobsRes : []);
      } catch (error) {
        console.error('Failed to load filter options:', error);
      }
    };
    loadOptions();
  }, []);

  // Filter templates by selected campaign
  useEffect(() => {
    if (query.customer) {
      const campaign = campaigns.find(c => c.campaignName === query.customer);
      if (campaign && campaign.templates) {
        const campaignTemplates = templates.filter(t =>
          campaign.templates.includes(t)
        );
        setFilteredTemplates(campaignTemplates);
      } else {
        setFilteredTemplates(templates);
      }
    } else {
      setFilteredTemplates(templates);
    }
  }, [query.customer, campaigns, templates]);

  const updateQuery = (value, field) => {
    console.log('updateQuery called with field:', field, 'value:', value);
    setQuery(prev => {
      const updated = { ...prev, [field]: value };
      console.log('Updated query before cleanup:', updated);
      Object.keys(updated).forEach(k => {
        const v = updated[k];
        if (v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
          delete updated[k];
        }
      });
      console.log('Updated query after cleanup:', updated);
      return updated;
    });
  };

  const loadQuickSearch = (quickSearch) => {
    setQuery(quickSearch.query);
    fetchResults(quickSearch.query);
  };

  const fetchResults = async (params = {}) => {
    setIsSearching(true);
    try {
      const data = await search(params);
      console.log('data:', data);

      const records = Array.isArray(data) ? data : (data.Records || data.Items || data.items || []);

      // const records = data.Records || data.Items || data.items || [];
      setResults(records);
      console.log(records)
    } catch (err) {
      console.error(err);
      setAlert && setAlert({
        variant: 'error',
        heading: 'Search Error',
        text: 'Failed to perform search.'
      });
    } finally {
      setIsSearching(false);
    }
  };

  const clearAll = () => {
    setQuery({});
    setResults([]);
  };

  

  const performSearch = async () => {
    await fetchResults(query);
  };

  const exportResults = (format) => {
    if (format === 'csv') {
      const headers = ['Timestamp', 'Job Name', 'Agent', 'Campaign', 'Template', 'QA Score', 'Compliance', 'Duration', 'GUID'];
      const rows = results.map(item => [
        new Date(item.timestamp).toISOString(),
        item.jobName || '',
        item.agent || '',
        item.customer || '',
        item.template || '',
        item.Result?.QA_Score ?? 'N/A',
        item.Result?.Compliance ? 'Pass' : 'Fail',
        item.duration || '',
        item.guid || ''
      ]);
      const csv = [headers, ...rows].map(row => row.join(',')).join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `search-results-${Date.now()}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } else if (format === 'json') {
      const json = JSON.stringify(results, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `search-results-${Date.now()}.json`;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const formatFilterLabel = (key) => {
    const labels = {
      agent: 'Agent',
      customer: 'Campaign',
      template: 'Template',
      qaScoreMin: 'Min Score',
      qaScoreMax: 'Max Score',
      compliance: 'Compliance',
      assessed: 'Assessment Status',
      status: 'Status',
      timestampFrom: 'From',
      timestampTo: 'To',
      jobId: 'Job ID',
      assignedTo: 'Assigned To',
      durationMin: 'Min Duration',
      durationMax: 'Max Duration',
      language: 'Language',
      entity: 'Entities',
      jobName: 'Job Name',
      msisdn: 'MSISDN'
    };
    return labels[key] || key;
  };

  const formatFilterValue = (key, value) => {
    if (key === 'timestampFrom' || key === 'timestampTo') {
      return new Date(parseInt(value)).toLocaleDateString();
    }
    if (key === 'compliance') {
      return value === 'true' ? 'Compliant' : 'Non-Compliant';
    }
    if (key === 'assessed') {
      return value === 'true' ? 'Assessed' : 'Not Assessed';
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value;
  };

  const removeAgentFromFilter = (agentToRemove) => {
    if (Array.isArray(query.agent)) {
      const updatedAgents = query.agent.filter(a => a !== agentToRemove);
      updateQuery(updatedAgents.length > 0 ? updatedAgents : null, 'agent');
    } else {
      updateQuery(null, 'agent');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Multi-Layered Search</h1>
        <div className="mt-2 p-3 bg-blue-50 border-l-4 border-blue-400 rounded text-sm text-blue-900">
          <p className="font-medium mb-2">Search Behavior & Performance:</p>
          <div className="space-y-2">
            <div>
              <p className="font-semibold text-blue-800 mb-1">Database-Level Filters (Fast):</p>
              <p className="ml-4 text-xs">These pull optimized records directly from indexed data:</p>
              <ul className="list-disc list-inside ml-6 space-y-0.5 text-xs">
                <li>Agent, Campaign, Template, Language, Entity</li>
                <li>Assigned To, Assessment Status, Compliance</li>
                <li>Date Range (Timestamp), QA Score Range</li>
                <li>Sentiment filters</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-blue-800 mb-1">Post-Processing Filters (Applied After Database Pull):</p>
              <p className="ml-4 text-xs">These refine results from database queries:</p>
              <ul className="list-disc list-inside ml-6 space-y-0.5 text-xs">
                <li>Exact QA Score Range (refines bucket results)</li>
                <li>Duration Range, Status</li>
                <li>GUID, Job ID, Job Name</li>
                <li>Confidence Minimum</li>
              </ul>
            </div>
            <div className="pt-1 border-t border-blue-200">
              <p className="font-semibold text-blue-800 mb-1">Filter Logic:</p>
              <ul className="list-disc list-inside ml-4 space-y-0.5 text-xs">
                <li><strong>Multiple Agents or QA Score Ranges:</strong> OR logic (returns any match)</li>
                <li><strong>All Other Filters:</strong> AND logic (results must match all criteria)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('quick')}
            className={`${
              activeTab === 'quick'
                ? 'border-cyan-500 text-cyan-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Quick Filters
          </button>
          <button
            onClick={() => setActiveTab('basic')}
            className={`${
              activeTab === 'basic'
                ? 'border-cyan-500 text-cyan-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Basic Search
          </button>
          <button
            onClick={() => setActiveTab('advanced')}
            className={`${
              activeTab === 'advanced'
                ? 'border-cyan-500 text-cyan-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Advanced
          </button>
        </nav>
      </div>

      {/* Quick Filters Tab */}
      {activeTab === 'quick' && (
        <div className="card space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Quick Search Shortcuts</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {QUICK_SEARCHES.map(qs => (
              <button
                key={qs.name}
                onClick={() => loadQuickSearch(qs)}
                className="p-4 border border-gray-300 rounded-lg hover:bg-gray-50 text-left transition"
              >
                <div className="text-2xl mb-2">{qs.icon}</div>
                <div className="font-medium text-gray-900">{qs.name}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Basic Search Tab */}
      {activeTab === 'basic' && (
        <div className="card space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Agent Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Agent</label>
              <MultiSelect
                options={agents.map(a => ({ value: a, label: a }))}
                value={query.agent && Array.isArray(query.agent) ? query.agent : []}
                onChange={values => {
                  console.log('MultiSelect onChange received:', values);
                  updateQuery(values && values.length > 0 ? values : null, 'agent');
                }}
                placeholder="Select agents..."
              />
            </div>

            {/* Campaign Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
              <Select
                options={campaigns.map(c => ({
                  value: c.campaignName,
                  label: c.campaignName
                }))}
                value={query.customer ? { value: query.customer, label: query.customer } : null}
                onChange={opt => {
                  updateQuery(opt?.value, 'customer');
                  if (!opt) updateQuery(null, 'template'); // Clear template if campaign cleared
                }}
                placeholder="Select campaign..."
                isClearable
              />
            </div>

            {/* Template Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
              <Select
                options={filteredTemplates.map(t => ({ value: t, label: t }))}
                value={query.template ? { value: query.template, label: query.template } : null}
                onChange={opt => updateQuery(opt?.value, 'template')}
                placeholder="Select template..."
                isClearable
                isDisabled={!query.customer}
              />
              {!query.customer && (
                <p className="text-xs text-gray-500 mt-1">Select a campaign first</p>
              )}
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <div className="relative">
                <DatePicker
                  selectsRange
                  startDate={query.timestampFrom ? new Date(parseInt(query.timestampFrom)) : null}
                  endDate={query.timestampTo ? new Date(parseInt(query.timestampTo)) : null}
                  onChange={(dates) => {
                    const [start, end] = dates;
                    if (start) {
                      updateQuery(start.getTime(), 'timestampFrom');
                    } else {
                      updateQuery(null, 'timestampFrom');
                    }
                    if (end) {
                      updateQuery(new Date(end).setUTCHours(23, 59, 59, 999), 'timestampTo');
                    } else {
                      updateQuery(null, 'timestampTo');
                    }
                  }}
                  maxDate={new Date()}
                  className="input-field w-full"
                  placeholderText="Select date range"
                  isClearable
                  dateFormat="MMM dd, yyyy"
                  wrapperClassName="w-full"
                />
              </div>
            </div>

            {/* QA Score Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">QA Score Range</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="Min"
                  value={query.qaScoreMin || ''}
                  onChange={e => updateQuery(e.target.value, 'qaScoreMin')}
                  className="input-field"
                />
                <span>to</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="Max"
                  value={query.qaScoreMax || ''}
                  onChange={e => updateQuery(e.target.value, 'qaScoreMax')}
                  className="input-field"
                />
              </div>
            </div>

            {/* Compliance Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Compliance</label>
              <Select
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'true', label: 'Compliant Only' },
                  { value: 'false', label: 'Non-Compliant Only' }
                ]}
                value={query.compliance ?
                  { value: query.compliance, label: query.compliance === 'true' ? 'Compliant Only' : 'Non-Compliant Only' }
                  : { value: 'all', label: 'All' }
                }
                onChange={opt => updateQuery(opt?.value === 'all' ? null : opt?.value, 'compliance')}
              />
            </div>
          </div>

          <div className="flex space-x-2">
            <button className="btn-primary" onClick={performSearch}>
              Search
            </button>
            <button className="btn-outline" onClick={clearAll}>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Advanced Search Tab */}
      {activeTab === 'advanced' && (
        <div className="card space-y-6">
          {/* All filters from Basic + Additional advanced filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Job ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job ID</label>
              <Select
                options={jobs.map(j => ({ value: j, label: j }))}
                value={query.jobId ? { value: query.jobId, label: query.jobId } : null}
                onChange={opt => updateQuery(opt?.value, 'jobId')}
                placeholder="Select job..."
                isClearable
              />
            </div>

            {/* Assigned To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
              <Select
                options={assignedToOptions.map(a => ({ value: a, label: a }))}
                value={query.assignedTo ? { value: query.assignedTo, label: query.assignedTo } : null}
                onChange={opt => updateQuery(opt?.value, 'assignedTo')}
                placeholder="Select QA analyst..."
                isClearable
              />
            </div>

            {/* GUID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">GUID</label>
              <input
                type="text"
                value={query.guid || ''}
                onChange={e => updateQuery(e.target.value, 'guid')}
                placeholder="Enter GUID..."
                className="input-field"
              />
            </div>

            {/* Assessment Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assessment Status</label>
              <Select
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'true', label: 'Assessed Only' },
                  { value: 'false', label: 'Not Assessed' }
                ]}
                value={query.assessed ?
                  { value: query.assessed, label: query.assessed === 'true' ? 'Assessed Only' : 'Not Assessed' }
                  : { value: 'all', label: 'All' }
                }
                onChange={opt => updateQuery(opt?.value === 'all' ? null : opt?.value, 'assessed')}
              />
            </div>

            {/* Status
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <Select
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'Done', label: 'Completed' },
                  { value: 'InProgress', label: 'Processing' },
                  { value: 'Failed', label: 'Failed' }
                ]}
                value={query.status ?
                  { value: query.status, label: query.status }
                  : { value: 'all', label: 'All' }
                }
                onChange={opt => updateQuery(opt?.value === 'all' ? null : opt?.value, 'status')}
              />
            </div> */}

            {/* Duration Range */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration (seconds)</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  placeholder="Min"
                  value={query.durationMin || ''}
                  onChange={e => updateQuery(e.target.value, 'durationMin')}
                  className="input-field"
                />
                <span>to</span>
                <input
                  type="number"
                  min="0"
                  placeholder="Max"
                  value={query.durationMax || ''}
                  onChange={e => updateQuery(e.target.value, 'durationMax')}
                  className="input-field"
                />
              </div>
            </div>
          </div>

          <div className="flex space-x-2">
            <button className="btn-primary" onClick={performSearch}>
              Search
            </button>
            <button className="btn-outline" onClick={clearAll}>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      {Object.keys(query).length > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-4 bg-gray-50 rounded-lg">
          <span className="text-sm font-medium text-gray-700">Active Filters:</span>
          {(() => {
            console.log('Query object:', query);
            return null;
          })()}
          {Object.entries(query).flatMap(([key, value]) => {
            console.log(`Filter key: ${key}, value:`, value, 'isArray:', Array.isArray(value));
            // Handle agent array separately to show individual chips
            if (key === 'agent' && Array.isArray(value)) {
              return value.filter(agent => agent != null).map(agent => (
                <span key={`agent-${agent}`} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-cyan-100 text-cyan-800">
                  Agent: {agent}
                  <button
                    onClick={() => removeAgentFromFilter(agent)}
                    className="ml-2 hover:text-cyan-900 font-bold"
                  >
                    ✕
                  </button>
                </span>
              ));
            }
            // Regular filters
            return (
              <span key={key} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-cyan-100 text-cyan-800">
                {formatFilterLabel(key)}: {formatFilterValue(key, value)}
                <button
                  onClick={() => updateQuery(null, key)}
                  className="ml-2 hover:text-cyan-900 font-bold"
                >
                  ✕
                </button>
              </span>
            );
          })}
          <button
            onClick={clearAll}
            className="text-sm text-cyan-600 hover:text-cyan-800 font-medium"
          >
            Clear All
          </button>
        </div>
      )}

      {/* Results Table */}
      {results.length > 0 && (
        <ContactTable
          data={results}
          loading={isSearching}
          empty={<NoMatches />}
          header={
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Search Results ({results.length})
              </h2>
              <div className="flex space-x-2">
                <button
                  onClick={() => exportResults('csv')}
                  className="btn-outline"
                >
                  Export CSV
                </button>
                <button
                  onClick={() => exportResults('json')}
                  className="btn-outline"
                >
                  Export JSON
                </button>
              </div>
            </div>
          }
          onRowClick={item => navigate(`/dashboard/${item.key || item.filename}`)}
        />
      )}
    </div>
  );
};

const NoMatches = () => (
  <div className="text-center py-12">
    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
    <h3 className="mt-2 text-sm font-medium text-gray-900">No results found</h3>
    <p className="mt-1 text-sm text-gray-500">Try different filters or clear some criteria.</p>
  </div>
);

// Helper function
function getStartOfWeek() {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = now.getDate() - dayOfWeek;
  return new Date(now.setDate(diff)).setHours(0, 0, 0, 0);
}

export default Search;